/**
 * ログインメールアドレス変更の確認（**認証不要**の着地ページ）。
 *
 * ここが無いあいだ、ログインメールの変更は**入口も出口も無い**機能だった。
 * `POST /auth/portal/me/email` が送る確認メールのリンクは
 * `${PORTAL_ORIGIN}/confirm-email?token=...`（`routes/auth/common.ts`）で固定
 * なのに、SPA にそのパスが無く 404 に落ちていた（`/reset-password` が
 * 同じ理由で行き止まりだったのと完全に同型）。**パスは変えないこと。**
 *
 * 守っていること:
 *   1. **認証を要求しない。** 確認リンクは新しいアドレスの受信箱にしか届かず、
 *      トークンそのものが「そのアドレスに到達できる」証明になっている。
 *      別端末でメールを開いた人が確定できないと、ほとんどの利用者が詰まる。
 *   2. **自動で確定しない。** ページを開いただけで POST すると、メール
 *      クライアントのリンク先読みだけでアドレスが変わり得る。ボタンを 1 回
 *      押してもらう（`/forwards/verify` と同じ作法）。
 *   3. 成功しても自動ログインしない。サーバは確定と同時に全セッションを
 *      切るので、ログイン画面へ誘導するのが唯一の正しい導線。
 */
import * as React from 'react';
import { useMutation } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@mail-sv/ui';
import { AtSign, CheckCircle2 } from 'lucide-react';
import { confirmEmailChange } from '../lib/auth.js';
import { FormAlert } from '../components/form.js';
import { useSearchParams } from '../lib/nav.js';
import { env } from '../env.js';

export function ConfirmEmailPage() {
  const search = useSearchParams();
  // トークンは URL からだけ読む（state にもフォームにも入れない）。
  const token = typeof search.token === 'string' ? search.token : '';

  const submit = useMutation({
    mutationFn: () => confirmEmailChange(token),
  });

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div className="w-full max-w-md space-y-6">
        <div className="space-y-2 text-center">
          <div className="brand-gradient mx-auto flex size-12 items-center justify-center rounded-xl text-white shadow-lg shadow-primary/25">
            <AtSign className="size-6" aria-hidden />
          </div>
          <h1 className="text-xl font-semibold">{env.brandName}</h1>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>ログイン用メールアドレスの変更</CardTitle>
            <CardDescription>
              新しいメールアドレスにお送りした確認リンクです。内容をご確認のうえ、変更を確定してください。
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            {submit.isSuccess ? (
              <>
                <Alert variant="success">
                  <AlertTitle className="flex items-center gap-2">
                    <CheckCircle2 className="size-4" aria-hidden />
                    メールアドレスを変更しました
                  </AlertTitle>
                  <AlertDescription>
                    これからは <span className="font-medium">{submit.data.email}</span>{' '}
                    でログインしてください。
                    安全のため、これまでのログインはすべて無効になりました。
                  </AlertDescription>
                </Alert>
                <Button asChild className="w-full">
                  <Link to="/login">ログイン画面へ</Link>
                </Button>
              </>
            ) : token.length === 0 ? (
              /*
               * トークンが無い＝メールのリンクを経由していない。
               * 押せるものが無い画面を出しても仕方がないので、
               * 申請をやり直す場所（アカウント設定）へ案内する。
               */
              <>
                <Alert variant="warning">
                  <AlertTitle>リンクが正しくありません</AlertTitle>
                  <AlertDescription>
                    メール内のリンクをそのまま開いてください。
                    有効期限が切れている場合は、コントロールパネルの「アカウント設定」から
                    もう一度変更をお申し込みください。
                  </AlertDescription>
                </Alert>
                <Button asChild variant="outline" className="w-full">
                  <Link to="/login">ログイン画面へ</Link>
                </Button>
              </>
            ) : (
              <>
                <FormAlert error={submit.error} />

                <Alert variant="info">
                  <AlertTitle>変更されるのはログイン用のアドレスだけです</AlertTitle>
                  <AlertDescription>
                    コントロールパネルにログインするためのアドレスが変わります。
                    メールの送受信に使うアドレス（メールソフトの設定）は変わりません。
                  </AlertDescription>
                </Alert>

                <Button
                  className="w-full"
                  loading={submit.isPending}
                  onClick={() => submit.mutate()}
                >
                  メールアドレスの変更を確定する
                </Button>
              </>
            )}
          </CardContent>
        </Card>

        <p className="text-center text-sm text-muted-foreground">
          <Link to="/login" className="underline">
            ログイン画面へ戻る
          </Link>
        </p>
      </div>
    </div>
  );
}
